import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Megaphone, ArrowLeft, CheckCircle2, XCircle, Trash2, Radio, Users2, Rss,
} from "lucide-react";
import { api, idr, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggal } from "@/lib/tanggal";
import {
  PageHeader, TidakPunyaAkses, StatusBadge, CAMPAIGN_STATUS_META, CHANNEL_LABEL,
  adaField, usePinHapus, PinHapusDialog,
} from "./_shared";

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const bolehLihat = can("marketing_campaign", "view");
  const bolehApprove = can("marketing_campaign", "update");
  const bolehHapus = can("marketing_campaign", "delete");
  const { dialog: pinDialog, setDialog: setPinDialog, hapusDenganPin, submitDialog } = usePinHapus();

  const { data: campaign, loading, error, refetch } = useDataQuery(
    ["marketing", "campaign", id],
    () => api.get(`/marketing/campaigns/${id}`).then((r) => r.data),
    { enabled: bolehLihat },
  );
  const { data: metrics, loading: loadingMetrics } = useDataQuery(
    ["marketing", "campaign-metrics", id],
    () => api.get(`/marketing/campaigns/${id}/metrics`).then((r) => r.data),
    { enabled: bolehLihat },
  );

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Megaphone} title="Detail Campaign" />
        <TidakPunyaAkses label="Campaign Marketing" testid="mkt-campdet-tanpa-akses" />
      </div>
    );
  }

  const setujui = async () => {
    if (!window.confirm(`Setujui campaign "${campaign.nama}"?`)) return;
    try {
      const r = await api.post(`/marketing/campaigns/${id}/approve`);
      toast.success(r.data.message || "Campaign disetujui");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetujui campaign"); }
  };

  const tolak = async () => {
    const alasan = window.prompt(`Alasan menolak campaign "${campaign.nama}"?`) || "";
    if (!alasan.trim()) return;
    try {
      const r = await api.post(`/marketing/campaigns/${id}/reject`, { alasan });
      toast.success(r.data.message || "Campaign ditolak");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menolak campaign"); }
  };

  const hapus = async () => {
    if (!window.confirm(`Hapus campaign "${campaign.nama}"?`)) return;
    const aksi = (pin) => api.delete(`/marketing/campaigns/${id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_campaign", targetId: id, ringkasan: `Hapus campaign ${campaign.nama}` });
    if (langsung) { toast.success("Campaign dihapus"); navigate("/marketing/campaigns"); }
  };

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-campaign-detail">
      <Link to="/marketing/campaigns" data-testid="mkt-campdet-kembali"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" /> Kembali ke daftar campaign
      </Link>

      <LoadingState loading={loading} error={error} onRetry={refetch} varian="kartu" data-testid="mkt-campdet-loading">
        {campaign && (
          <>
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-mono text-xs text-slate-400">{campaign.code}</p>
                  <h1 className="font-display text-xl md:text-2xl font-bold text-slate-900 mt-0.5 inline-flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-emerald-600" /> {campaign.nama}
                  </h1>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <StatusBadge meta={CAMPAIGN_STATUS_META} value={campaign.status} />
                    <span className="text-xs text-slate-500">{campaign.brand || "-"}{campaign.cabang ? ` / ${campaign.cabang}` : ""}</span>
                    <span className="text-xs text-slate-500">{formatTanggal(campaign.tanggal_mulai)} – {formatTanggal(campaign.tanggal_selesai)}</span>
                  </div>
                  {campaign.tujuan && <p className="text-sm text-slate-600 mt-2 max-w-2xl">{campaign.tujuan}</p>}
                  {campaign.status === "rejected" && campaign.rejection_reason && (
                    <p className="text-xs text-rose-600 mt-2">Alasan ditolak: {campaign.rejection_reason}</p>
                  )}
                  <p className="text-[11px] text-slate-400 mt-2">
                    Diajukan oleh <span className="font-medium text-slate-600">{campaign.created_by_name || campaign.created_by}</span>
                    {campaign.approved_by_name && <> · Disetujui oleh <span className="font-medium text-slate-600">{campaign.approved_by_name}</span></>}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {campaign.status === "pending_approval" && bolehApprove && (
                    <>
                      <button onClick={setujui} data-testid="mkt-campdet-setujui"
                              className="h-9 px-3 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-medium inline-flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4" /> Setujui
                      </button>
                      <button onClick={tolak} data-testid="mkt-campdet-tolak"
                              className="h-9 px-3 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-sm font-medium inline-flex items-center gap-1.5">
                        <XCircle className="w-4 h-4" /> Tolak
                      </button>
                    </>
                  )}
                  {["pending_approval", "rejected"].includes(campaign.status) && bolehHapus && (
                    <button onClick={hapus} data-testid="mkt-campdet-hapus"
                            className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-rose-50 text-rose-600 text-sm font-medium inline-flex items-center gap-1.5">
                      <Trash2 className="w-4 h-4" /> Hapus
                    </button>
                  )}
                </div>
              </div>

              {/* Line items */}
              {campaign.line_items?.length > 0 && (
                <div className="mt-4 border-t border-slate-100 pt-4">
                  <p className="text-xs font-semibold text-slate-700 mb-2">Alokasi Budget per Channel</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-slate-500 text-xs">
                        <tr className="text-left"><th className="py-1.5 pr-3">Channel</th><th className="py-1.5 pr-3 text-right">Alokasi</th><th className="py-1.5">Keterangan</th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {campaign.line_items.map((li, idx) => (
                          <tr key={idx}>
                            <td className="py-1.5 pr-3 text-slate-700">{CHANNEL_LABEL[li.channel] || li.channel}</td>
                            <td className="py-1.5 pr-3 text-right font-mono">{idr(li.alokasi)}</td>
                            <td className="py-1.5 text-slate-500">{li.keterangan || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5">Total alokasi: <span className="font-mono font-semibold">{idr(campaign.total_alokasi)}</span> dari budget <span className="font-mono font-semibold">{idr(campaign.budget_plan)}</span></p>
                </div>
              )}
            </div>

            {/* Metrics */}
            <LoadingState loading={loadingMetrics} varian="kartu" jumlahKartu={4} data-testid="mkt-campdet-metrics-loading">
              {metrics && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard label="Total Spend" value={idr(metrics.spend.total)} testid="mkt-campdet-m-spend" />
                    <MetricCard label="Omzet Teratribusi" value={idr(metrics.omzet)} testid="mkt-campdet-m-omzet" />
                    <MetricCard label="ROAS" value={metrics.roas != null ? `${metrics.roas.toFixed(2)}x` : "-"} testid="mkt-campdet-m-roas" aksen="emerald" />
                    {adaField(metrics, "roi") ? (
                      <MetricCard label="ROI" value={metrics.roi != null ? `${(metrics.roi * 100).toFixed(1)}%` : "-"} testid="mkt-campdet-m-roi" />
                    ) : (
                      <MetricCard label="CAC (per Deal)" value={metrics.cac != null ? idr(metrics.cac) : "-"} testid="mkt-campdet-m-cac" />
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <MiniStat label="Impressions" value={fmtNum(metrics.impressions)} />
                    <MiniStat label="Klik" value={fmtNum(metrics.clicks)} />
                    <MiniStat label="Video Views" value={fmtNum(metrics.video_views)} />
                    <MiniStat label="Engagements" value={fmtNum(metrics.engagements)} />
                    <MiniStat label="Lead" value={fmtNum(metrics.jumlah_lead)} />
                    <MiniStat label="Closed Won" value={fmtNum(metrics.closed_won)} />
                  </div>
                  {adaField(metrics, "margin") && (
                    <p className="text-xs text-slate-500">Margin teratribusi: <span className="font-mono font-semibold text-slate-700">{idr(metrics.margin)}</span></p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <SpendChannel label="Iklan Digital" value={metrics.spend.ads_digital} />
                    <SpendChannel label="Endorsement & KOL" value={metrics.spend.kol_endorsement} />
                    <SpendChannel label="Promosi Media" value={metrics.spend.promosi_media} />
                  </div>
                </div>
              )}
            </LoadingState>

            {/* Tautan cepat ke data terkait campaign ini */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <TautanTerkait to={`/marketing/ads?campaign_id=${id}`} icon={Radio} label="Log Iklan (Ads)" testid="mkt-campdet-link-ads" />
              <TautanTerkait to={`/marketing/pfp?campaign_id=${id}`} icon={Rss} label="Placement PFP" testid="mkt-campdet-link-pfp" />
              <TautanTerkait to={`/marketing/leads?campaign_id=${id}`} icon={Users2} label="Leads Campaign Ini" testid="mkt-campdet-link-leads" />
            </div>
          </>
        )}
      </LoadingState>

      <PinHapusDialog dialog={pinDialog} setDialog={setPinDialog} onSubmit={() => submitDialog(() => navigate("/marketing/campaigns"))} />
    </div>
  );
}

function MetricCard({ label, value, testid, aksen = "slate" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={testid}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-xl font-bold mt-0.5 font-mono ${aksen === "emerald" ? "text-emerald-700" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="border border-slate-100 rounded-lg p-2.5 bg-white">
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className="text-base font-bold text-slate-900 font-mono">{value}</p>
    </div>
  );
}

function SpendChannel({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white flex items-center justify-between">
      <span className="text-xs text-slate-600">{label}</span>
      <span className="text-sm font-mono font-semibold text-slate-900">{idr(value)}</span>
    </div>
  );
}

function TautanTerkait({ to, icon: Icon, label, testid }) {
  return (
    <Link to={to} data-testid={testid}
          className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex items-center gap-3 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors">
      <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <span className="text-sm font-medium text-slate-800">{label}</span>
    </Link>
  );
}
